import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Plus, Edit, Search, Printer, FileText, Award, TrendingUp, Download, Upload, MessageCircle, Eye, Power } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"];

// WhatsApp URL builder — auto-add +91, opens native wa.me link
export const waLink = (phone, message) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  const withCc = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCc}?text=${encodeURIComponent(message)}`;
};

export default function Students() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const [school, setSchool] = useState(null);
  const [items, setItems] = useState([]);
  const [myClasses, setMyClasses] = useState([]);
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState(isTeacher ? "mine" : "all");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [exams, setExams] = useState([]);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [tcOpen, setTcOpen] = useState(false);
  const [tcStudent, setTcStudent] = useState(null);
  const [tc, setTc] = useState({ tc_number: "", reason: "Transfer to another school", conduct: "Good", date_leaving: new Date().toISOString().slice(0,10) });
  const [promote, setPromote] = useState({ from_class: "1", to_class: "2" });

  const toggleStatus = async (s) => {
    const newStatus = s.status === "inactive" ? "active" : "inactive";
    if (!window.confirm(`Mark ${s.first_name} as ${newStatus.toUpperCase()}? This will ${newStatus === "inactive" ? "PAUSE fees, attendance, reports & family login" : "resume everything"}.`)) return;
    try {
      await api.put(`/students/${s.id}/status`, { status: newStatus });
      toast.success(`${s.first_name} marked ${newStatus}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendWaFee = async (s) => {
    const parentPhone = s.parent_details?.father?.mobile || s.parent_details?.mother?.mobile || s.preferred_mob;
    if (!parentPhone) { toast.error("No parent WhatsApp number on file"); return; }
    let due = 0;
    try {
      const { data } = await api.get(`/fee/ledger/${s.id}`);
      due = data.due || 0;
    } catch (e) { /* ignore */ }
    const schoolName = school?.name || "School";
    const msg = `Dear Parent,\n\nWith due respect, this is a gentle reminder from ${schoolName}.\n\nOutstanding fee for ${s.first_name} ${s.last_name || ""} (Class ${s.class_name}${s.section ? " " + s.section : ""}, Reg: ${s.registration_no || "—"}) is *₹${due.toLocaleString("en-IN")}*.\n\nKindly clear the dues at your earliest convenience so that regular services remain uninterrupted.\n\nThank you for your continued support.\n— ${schoolName} Fee Office`;
    const url = waLink(parentPhone, msg);
    if (url) window.open(url, "_blank");
  };

  const openView = (s) => { window.location.href = `/school/students/${s.id}`; };

  const load = async () => { const { data } = await api.get("/students", { params: { q } }); setItems(data); };
  useEffect(() => {
    load();
    api.get("/exams").then(r => setExams(r.data));
    if (user?.school_id) api.get(`/schools/${user.school_id}`).then(r => setSchool(r.data)).catch(() => {});
    if (isTeacher) {
      api.get("/stats/teacher").then((r) => {
        const mc = r.data?.my_classes || [];
        setMyClasses(mc);
        if (mc.length === 0) setClassFilter("all"); // no assignments yet — show all instead of an empty list
      }).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [user?.school_id]);

  const printCard = (s, exam) => {
    const w = window.open("", "_blank");
    const isAdmit = !!exam;
    const sig = school?.principal_signature_url || "";
    const principalName = school?.principal_name || "Principal";
    const schoolName = school?.name || "School Dial Institute";
    const schoolLogo = school?.logo_url || "";
    const schoolAddr = [school?.city, school?.state].filter(Boolean).join(", ");
    w.document.write(`
      <html><head><title>${isAdmit ? "Admit Card" : "iCard"} - ${s.first_name}</title>
      <style>body{font-family:'Segoe UI',sans-serif;padding:40px;background:#f8f9fa}
      .card{border:2px solid #0f172a;padding:32px;width:500px;border-radius:12px;background:white;box-shadow:0 4px 20px rgba(0,0,0,0.08)}
      .brand{display:flex;align-items:center;gap:12px;margin-bottom:12px}
      .brand img{height:44px;width:44px;object-fit:contain}
      .brand h3{margin:0;font-size:16px;color:#0f172a}
      .brand p{margin:0;font-size:11px;color:#64748b}
      .head{border-bottom:2px solid #f59e0b;padding-bottom:16px;margin-bottom:20px}
      .head h1{margin:0;font-size:14px;color:#64748b;text-transform:uppercase;letter-spacing:2px}
      .head h2{margin:6px 0 0;font-size:22px;color:#0f172a}
      .row{display:flex;gap:8px;font-size:14px;margin:10px 0;padding:6px 0;border-bottom:1px dashed #e2e8f0}
      .k{color:#64748b;width:160px;font-weight:600}
      .v{color:#0f172a;font-weight:700}
      .sig-block{margin-top:36px;display:flex;flex-direction:column;align-items:flex-end;gap:4px}
      .sig-block img{height:48px;max-width:180px;object-fit:contain}
      .sig-line{border-top:1px solid #0f172a;min-width:180px;padding-top:4px;font-size:12px;color:#0f172a;font-weight:600;text-align:center}
      .sig-role{font-size:11px;color:#64748b;text-align:center}
      </style></head><body>
      <div class="card">
        ${(schoolLogo || schoolName) ? `<div class="brand">${schoolLogo ? `<img src="${schoolLogo}" alt="logo" />` : ""}<div><h3>${schoolName}</h3><p>${schoolAddr}</p></div></div>` : ""}
        <div class="head"><h1>${isAdmit ? "Examination Admit Card" : "Student iCard"}</h1><h2>${s.first_name} ${s.last_name || ""}</h2></div>
        ${s.photo_url ? `<div style="text-align:center;margin-bottom:16px"><img src="${s.photo_url}" style="width:100px;height:120px;object-fit:cover;border:2px solid #0f172a" /></div>` : ""}
        <div class="row"><span class="k">Registration:</span><span class="v">${s.registration_no || "—"}</span></div>
        <div class="row"><span class="k">Class / Section:</span><span class="v">${s.class_name} ${s.section || ""}</span></div>
        <div class="row"><span class="k">Date of Birth:</span><span class="v">${s.dob || "—"}</span></div>
        <div class="row"><span class="k">Blood Group:</span><span class="v">${s.blood_group || "—"}</span></div>
        <div class="row"><span class="k">Contact:</span><span class="v">${s.preferred_mob || "—"}</span></div>
        ${isAdmit ? `
          <div class="row"><span class="k">Exam:</span><span class="v">${exam.name}</span></div>
          <div class="row"><span class="k">Exam Date:</span><span class="v">${exam.exam_date}</span></div>
          <div class="row"><span class="k">Max Marks:</span><span class="v">${exam.max_marks}</span></div>
        ` : ""}
        <div class="sig-block">
          ${sig ? `<img src="${sig}" alt="signature" crossorigin="anonymous" />` : ""}
          <div class="sig-line">${principalName}</div>
          <div class="sig-role">Principal</div>
        </div>
      </div>
      <script>setTimeout(function(){window.print()},400)</script></body></html>`);
  };

  const printTC = async (s) => {
    const w = window.open("", "_blank");
    const psig = school?.principal_signature_url || "";
    const csig = school?.chairman_signature_url || "";
    const principalName = school?.principal_name || "Principal";
    const chairmanName = school?.chairman_name || "";
    const schoolName = school?.name || "School Dial Institute";
    const schoolLogo = school?.logo_url || "";
    w.document.write(`
      <html><head><title>Transfer Certificate - ${s.first_name}</title>
      <style>body{font-family:'Georgia',serif;padding:60px;background:white}
      .doc{max-width:700px;margin:0 auto;border:3px double #0f172a;padding:40px}
      .brand{display:flex;align-items:center;gap:14px;justify-content:center;margin-bottom:16px}
      .brand img{height:56px;width:56px;object-fit:contain}
      .brand h3{margin:0;font-size:20px;color:#0f172a}
      h1{text-align:center;letter-spacing:4px;font-size:22px;color:#0f172a;margin:0 0 8px}
      h2{text-align:center;font-size:14px;color:#64748b;margin:0 0 30px;letter-spacing:2px;text-transform:uppercase}
      p{line-height:1.9;font-size:15px;color:#1e293b}
      .fill{display:inline-block;border-bottom:1px dotted #64748b;min-width:200px;font-weight:700;padding:0 4px}
      .sig{display:flex;justify-content:space-between;margin-top:80px;font-size:13px;color:#0f172a;gap:24px}
      .sig-col{flex:1;text-align:center}
      .sig-col img{height:48px;max-width:200px;object-fit:contain;display:block;margin:0 auto 4px}
      .sig-col .line{border-top:1px solid #0f172a;padding-top:4px;font-weight:600}
      .sig-col .role{font-size:11px;color:#64748b}
      </style></head><body><div class="doc">
      <div class="brand">${schoolLogo ? `<img src="${schoolLogo}" alt="logo" />` : ""}<h3>${schoolName}</h3></div>
      <h1>TRANSFER CERTIFICATE</h1>
      <h2>Issued by ${schoolName}</h2>
      <p><b>TC No.</b> <span class="fill">${tc.tc_number || "AUTO-" + Date.now().toString().slice(-6)}</span>
       &nbsp;&nbsp; <b>Date:</b> <span class="fill">${new Date().toLocaleDateString()}</span></p>
      <p>This is to certify that <span class="fill">${s.first_name} ${s.middle_name || ""} ${s.last_name || ""}</span>,
      Son/Daughter of <span class="fill">${s.parent_details?.father?.name || "—"}</span>,
      was a bonafide student of this institution, studying in Class <span class="fill">${s.class_name} ${s.section || ""}</span>.</p>
      <p><b>Date of Birth:</b> <span class="fill">${s.dob || "—"}</span> &nbsp;&nbsp;
         <b>Registration No.:</b> <span class="fill">${s.registration_no || "—"}</span></p>
      <p><b>Reason for leaving:</b> <span class="fill">${tc.reason}</span></p>
      <p><b>Conduct:</b> <span class="fill">${tc.conduct}</span> &nbsp;&nbsp;
         <b>Date of leaving:</b> <span class="fill">${tc.date_leaving}</span></p>
      <div class="sig">
        <div class="sig-col">
          ${psig ? `<img src="${psig}" crossorigin="anonymous" alt="principal sig" />` : ""}
          <div class="line">${principalName}</div><div class="role">Principal</div>
        </div>
        ${chairmanName || csig ? `<div class="sig-col">
          ${csig ? `<img src="${csig}" crossorigin="anonymous" alt="chairman sig" />` : ""}
          <div class="line">${chairmanName || "Chairman"}</div><div class="role">Chairman</div>
        </div>` : ""}
      </div>
      </div><script>setTimeout(function(){window.print()},400)</script></body></html>`);
    try {
      await api.post("/students/tc", { student_id: s.id, ...tc });
      toast.success("TC recorded");
      setTcOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doPromote = async () => {
    try {
      const { data } = await api.post("/students/promote", promote);
      toast.success(`Promoted ${data.promoted} students`);
      setPromoteOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashLayout variant={isTeacher ? "teacher" : "school"} title="Students">
      <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 border rounded px-3 bg-white flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name, reg no…" className="border-0 shadow-none focus-visible:ring-0" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="student-search" />
        </div>
        <div className="flex gap-2">
          <Select value={classFilter} onValueChange={(v) => { setClassFilter(v); setPage(1); }}>
            <SelectTrigger className="w-36" data-testid="filter-class"><SelectValue placeholder="Class" /></SelectTrigger>
            <SelectContent>
              {isTeacher && <SelectItem value="mine" data-testid="filter-class-mine">My Classes ({myClasses.length})</SelectItem>}
              <SelectItem value="all">All Classes</SelectItem>
              {CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sectionFilter} onValueChange={(v) => { setSectionFilter(v); setPage(1); }}>
            <SelectTrigger className="w-32" data-testid="filter-section"><SelectValue placeholder="Section" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sections</SelectItem>
              {["A","B","C","D","E"].map(x => <SelectItem key={x} value={x}>Sec {x}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-32" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="left">Left</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={async () => {
            const backend = process.env.REACT_APP_BACKEND_URL;
            const r = await fetch(`${backend}/api/students-export`, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "students.csv"; a.click();
          }} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" />Export</Button>
          <Link to="/school/admissions"><Button className="bg-blue-700 hover:bg-blue-800" data-testid="new-admission-btn"><Plus className="w-4 h-4 mr-2" />New</Button></Link>
        </div>
      </div>
      {(() => {
        const filtered = items.filter(s =>
          (classFilter === "all" || (classFilter === "mine" ? myClasses.includes(s.class_name) : s.class_name === classFilter)) &&
          (sectionFilter === "all" || s.section === sectionFilter) &&
          (statusFilter === "all" || (s.status || "active") === statusFilter) &&
          (!q || `${s.first_name} ${s.last_name || ""} ${s.registration_no || ""}`.toLowerCase().includes(q.toLowerCase()))
        );
        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const pageSafe = Math.min(page, totalPages);
        const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);
        return (
        <>
        <div className="text-xs text-slate-500 mb-2" data-testid="students-count">Showing {pageRows.length} of {filtered.length} students · Page {pageSafe}/{totalPages}</div>
        <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader><TableRow><TableHead>Reg No</TableHead><TableHead>Name</TableHead><TableHead>Class</TableHead><TableHead>Status</TableHead><TableHead>DOB</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {pageRows.map((s) => (
              <TableRow key={s.id} data-testid={`row-student-${s.id}`} className={s.status === "inactive" ? "opacity-50 bg-slate-50" : ""}>
                <TableCell className="font-mono text-xs">{s.registration_no || "—"}</TableCell>
                <TableCell className="font-semibold">{s.first_name} {s.last_name}</TableCell>
                <TableCell>{s.class_name} {s.section}</TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => toggleStatus(s)}
                    data-testid={`status-toggle-${s.id}`}
                    className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                      s.status === "inactive" ? "border-red-300 text-red-700 bg-red-50" :
                      s.status === "left" ? "border-slate-300 text-slate-600 bg-slate-100" :
                      "border-emerald-300 text-emerald-700 bg-emerald-50"
                    }`}
                  >
                    <Power className="w-3 h-3 inline mr-1" />
                    {(s.status || "active").toUpperCase()}
                  </button>
                </TableCell>
                <TableCell>{s.dob}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openView(s)} title="View Profile" data-testid={`view-student-${s.id}`}><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => sendWaFee(s)} title="WhatsApp Fee Reminder" data-testid={`wa-${s.id}`}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => printCard(s)} title="Print iCard" data-testid={`print-icard-${s.id}`}><Printer className="w-4 h-4" /></Button>
                  {exams.length > 0 && (
                    <Button size="sm" variant="ghost" onClick={() => printCard(s, exams[0])} title="Print Admit Card" data-testid={`print-admit-${s.id}`}><Award className="w-4 h-4" /></Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { setTcStudent(s); setTc({ ...tc, tc_number: "TC-" + Date.now().toString().slice(-6) }); setTcOpen(true); }} title="Issue TC" data-testid={`tc-${s.id}`}><FileText className="w-4 h-4" /></Button>
                  <Link to={`/school/admissions/${s.id}`}><Button size="sm" variant="ghost" data-testid={`edit-student-${s.id}`}><Edit className="w-4 h-4" /></Button></Link>
                </TableCell>
              </TableRow>
            ))}
            {pageRows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No students match the current filter.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="text-xs text-slate-500">Total: <b>{filtered.length}</b></div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" disabled={pageSafe <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="pg-prev">Prev</Button>
          <span className="text-sm font-mono">{pageSafe} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={pageSafe >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="pg-next">Next</Button>
        </div>
      </div>
        </>);
      })()}

      {/* Legacy profile dialog removed — replaced by /school/students/:id page route */}

      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent><DialogHeader><DialogTitle>Promote Class</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">From Class</Label>
                <Select value={promote.from_class} onValueChange={(v) => setPromote({ ...promote, from_class: v })}>
                  <SelectTrigger data-testid="promote-from"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="mb-1 block text-xs">To Class</Label>
                <Select value={promote.to_class} onValueChange={(v) => setPromote({ ...promote, to_class: v })}>
                  <SelectTrigger data-testid="promote-to"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">All students currently in class {promote.from_class} will be moved to class {promote.to_class}.</div>
          </div>
          <DialogFooter><Button onClick={doPromote} className="bg-blue-700 hover:bg-blue-800" data-testid="promote-submit">Promote</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tcOpen} onOpenChange={setTcOpen}>
        <DialogContent><DialogHeader><DialogTitle>Issue Transfer Certificate — {tcStudent?.first_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1 block text-xs">TC Number</Label><Input value={tc.tc_number} onChange={(e) => setTc({ ...tc, tc_number: e.target.value })} data-testid="tc-number" /></div>
            <div><Label className="mb-1 block text-xs">Reason</Label><Input value={tc.reason} onChange={(e) => setTc({ ...tc, reason: e.target.value })} data-testid="tc-reason" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Conduct</Label><Input value={tc.conduct} onChange={(e) => setTc({ ...tc, conduct: e.target.value })} data-testid="tc-conduct" /></div>
              <div><Label className="mb-1 block text-xs">Date of Leaving</Label><Input type="date" value={tc.date_leaving} onChange={(e) => setTc({ ...tc, date_leaving: e.target.value })} data-testid="tc-date" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={() => printTC(tcStudent)} className="bg-blue-700 hover:bg-blue-800" data-testid="tc-issue">Issue & Print</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
